import { Box, Typography } from '@mui/material';
import { ROUTE_PATH_COLORS } from '../constants/routeColors';
import type { RouteStructure, TrailerPath } from '../utils/routeInference';
import { getSegmentNodeId } from '../utils/routeInference';

export interface RouteVisualizationProps {
    routeStructure: RouteStructure | null;
    /** Resolve node id (eventId or storage:storageLocationId) to display label */
    getNodeLabel: (nodeId: string) => string;
    getTrailerName: (trailerId: string) => string;
}

/**
 * Text-based display of inferred route structure.
 * Shows trailer paths as "TrailerName: Source → Event A → Event B → Dest".
 */
export default function RouteVisualization({ routeStructure, getNodeLabel, getTrailerName }: RouteVisualizationProps) {
    if (!routeStructure) {
        return (
            <Typography variant="body2" color="text.secondary">
                Select sources and destinations to see route analysis.
            </Typography>
        );
    }

    const paths: TrailerPath[] = [];
    for (const path of Array.from(routeStructure.trailerPaths.values())) paths.push(path);
    if (paths.length === 0) {
        return (
            <Typography variant="body2" color="text.secondary">
                No trailer routes inferred. Add events with packing lists that include trailers.
            </Typography>
        );
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {paths.map((path, pathIndex) => {
                const segmentLabels = path.segments.map(s => getNodeLabel(getSegmentNodeId(s)));
                const routeText = segmentLabels.join(' → ');
                const lineColor = ROUTE_PATH_COLORS[pathIndex % ROUTE_PATH_COLORS.length];
                return (
                    <Typography
                        key={path.trailerId}
                        variant="body2"
                        component="span"
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            py: 0.5,
                            px: 1,
                            borderRadius: 1,
                            bgcolor: 'action.hover',
                            cursor: 'default',
                        }}
                    >
                        <Box
                            sx={{
                                width: 10,
                                height: 10,
                                borderRadius: '50%',
                                bgcolor: lineColor,
                                flexShrink: 0,
                            }}
                            aria-hidden
                        />
                        <span>
                            <strong>{getTrailerName(path.trailerId)}:</strong> {routeText}
                        </span>
                    </Typography>
                );
            })}
        </Box>
    );
}
